import { Cell, GoalCounts } from '../game/components/MapComponent';
import { MapDef } from '../game/defs/map';
import { PuzzleMapExporter } from '../game/util/PuzzleMapExporter';
import { BlockEntity } from '../game/entities/BlockEntity';
import { Match2ScoringConfig } from 'src/replicant/match2/match2.ruleset';

/**
 * Save state origin
 */
export type SaveStateOrigin =
  | 'boosterPurchase'
  | 'playOnApp'
  | 'outOfMoves'
  | 'cheat';

/**
 * Save state data
 */
export type SaveStateData = {
  level: number;
  visualMoves: number;
  moves: number;
  goalCounts: GoalCounts;
  mapDef: string;

  // New properties should be optional, as not everyone will have them available
  score?: number;

  // For scoring calculation tests
  scoringConfig?: Match2ScoringConfig;

  // Analytics properties
  continues: number;
  extraMoves: number;
  socialContinueMoves: number;
  adContinueMoves: number;
  usedBoosters: Record<string, number>;
  blocksBroken: Record<string, number>;
  powerBlocksCreated: Record<string, number>;
  blocksBrokenAnalytics: Record<string, number>;
};

/**
 * Extract save state data from current gameplay state
 */
export function getSaveStateData(params: {
  mapDef: MapDef;
  map: { columns: number; rows: number; grid: Cell[][] };
  level: number;
  score: number;
  visualMoves: number;
  moves: number;
  goalCounts: GoalCounts;
  continues: number;
  extraMoves: number;
  socialContinueMoves: number;
  adContinueMoves: number;
  usedBoosters: Record<string, number>;
  blocksBroken: Record<string, number>;
  powerBlocksCreated: Record<string, number>;
  blocksBrokenAnalytics: Record<string, number>;
  scoringConfig: Match2ScoringConfig;
}) {
  // Clone current map def
  const data = JSON.parse(JSON.stringify(params.mapDef)) as MapDef;

  // Remove all blocks from original grid, but preserve the enabled state
  for (let col = 0; col < data.grid.cells.length; col++) {
    for (let row = 0; row < data.grid.cells[col].length; row++) {
      data.grid.cells[col][row].blocks = [];
    }
  }

  // Save added entity uids to prevent duplicates
  const uids: number[] = [];

  // Current session map state
  const map = params.map;

  function addBlock(block: BlockEntity) {
    if (uids.includes(block.UID)) return;
    uids.push(block.UID);
    const blockId = block.c.block.blockId;
    const col = block.c.position.mapPosition.x;
    const row = block.c.position.mapPosition.y;
    data.grid.cells[col][row].enabled = true;
    data.grid.cells[col][row].blocks?.push({
      id: blockId,
      option: block.c.block.getGameplayState(),
    });
  }

  // Loop through each cell in map
  for (let column = 0; column < map.columns; ++column) {
    for (let row = 0; row < map.rows; ++row) {
      const cell = map.grid[column][row];

      // Push regular blocks
      const blockEntity = cell.base?.entity;
      if (blockEntity) addBlock(blockEntity);

      // Push overlay blocks
      const overlayEntity = cell.overlay?.entity;
      if (overlayEntity) addBlock(overlayEntity);
    }
  }

  // Encode map def to string
  const encodedMapDef = new PuzzleMapExporter(data).export();

  // Create complete state save data
  const state: SaveStateData = {
    level: params.level,
    visualMoves: params.visualMoves,
    moves: params.moves,
    goalCounts: { ...params.goalCounts },
    mapDef: encodedMapDef,
    score: params.score,

    // Analytics properties
    continues: params.continues,
    extraMoves: params.extraMoves,
    socialContinueMoves: params.socialContinueMoves,
    adContinueMoves: params.adContinueMoves,
    usedBoosters: params.usedBoosters,
    blocksBroken: params.blocksBroken,
    powerBlocksCreated: params.powerBlocksCreated,
    blocksBrokenAnalytics: params.blocksBrokenAnalytics,
    scoringConfig: params.scoringConfig,
  };

  return state;
}

/**
 * Encode save state data to string
 */
export function encodeSaveStateData(dataObj: SaveStateData) {
  return JSON.stringify(dataObj);
}

/**
 * Decode save state data from string
 */
export function decodeSaveStateData(dataStr: string) {
  return JSON.parse(dataStr) as SaveStateData;
}
